/**
 * Everything the certificate, the share buttons, the Open Graph tags and the embed snippets say about a lock, as pure functions of the lock.
 * The text is built from numbers and the lock's own fields; the locker's free-text title is only ever shown as a quotation and is cleaned here.
 */
import { DEFAULT_ORIGIN, PUBLIC_ORIGINS, lockPath } from '../config'
import { fmtDay, fmtShare, fmtSpan, fmtUsd, shortAddress } from '../format'
import type { LockSummary } from '../types'
import type { SealTone } from '../art/sealSvg'
import { isVesting, V2Terms, lockedAt, withdrawableAt } from './vesting'

export const certificateId = (l: Pick<LockSummary, 'type' | 'id'>) => `${l.type.toUpperCase()}-${l.id}`

/** Time served (0..1) for a plain or not-yet-started lock; the released share for a lock that is releasing; 1 once due or released. */
export function progressOf(l: LockSummary, now: number): number {
  if (l.phase === 'due' || l.phase === 'released') return 1
  if (l.phase === 'vesting' && l.vesting) {
    const amount = BigInt(l.amountRaw)
    if (amount === 0n) return 1
    const out = BigInt(l.vesting.unlockedRaw) + BigInt(l.vesting.withdrawableRaw)
    return Math.min(1, Math.max(0, Number((out * 10000n) / amount) / 10000))
  }
  const span = l.unlockAt - l.lockedAt
  return span <= 0 ? 1 : Math.min(1, Math.max(0, (now - l.lockedAt) / span))
}

export function sealToneOf(l: LockSummary): SealTone {
  if (l.phase === 'released') return 'released'
  if (l.phase === 'due') return 'due'
  if (l.phase === 'vesting') return 'vesting'
  return l.soon ? 'soon' : 'locked'
}

/** the locker's free text, made safe to quote: no control characters, no line breaks, at most 90 characters */
export function cleanDescription(s: string | undefined | null): string {
  // eslint-disable-next-line no-control-regex
  const t = (s ?? '').replace(/[\u0000-\u001f\u007f-\u009f\u200b-\u200f\u202a-\u202e\u2066-\u2069]/g, ' ').replace(/\s+/g, ' ').trim()
  return t.length > 90 ? `${t.slice(0, 89)}…` : t
}

/** The origin a page may print: the request's own when it is one of ours, production otherwise. */
export function safeOrigin(host: string | undefined | null, proto?: string | null): string {
  const h = (host ?? '').toLowerCase().split(',')[0].trim()
  const hit = PUBLIC_ORIGINS.find((o) => o.endsWith(`//${h}`))
  if (hit) return hit
  if (/^localhost:\d{2,5}$/.test(h) || h === 'localhost') return `${proto === 'https' ? 'https' : 'http'}://${h}`
  return DEFAULT_ORIGIN
}

export const certificateUrl = (origin: string, l: Pick<LockSummary, 'type' | 'id'>) => `${origin}${lockPath(l.type, l.id)}`
export const ogImageUrl = (origin: string, l: Pick<LockSummary, 'type' | 'id'>) => `${origin}/api/locks/og/${l.type}/${l.id}.png`
export const pngDownloadUrl = (origin: string, l: Pick<LockSummary, 'type' | 'id'>) => `${origin}/api/locks/og/${l.type}/${l.id}.png?scale=2`
export const badgeUrl = (origin: string, l: Pick<LockSummary, 'type' | 'id'>, style: 'flat' | 'card' = 'flat') =>
  `${origin}/api/locks/badge/${l.type}/${l.id}.svg${style === 'card' ? '?style=card' : ''}`

/** What is locked and how much of it, in a few words: "99.4% of the pool", "3% of the supply", "93% released". */
export function shareClause(l: LockSummary): string {
  if (l.phase === 'vesting' && l.vesting) {
    const amount = BigInt(l.amountRaw)
    const out = BigInt(l.vesting.unlockedRaw) + BigInt(l.vesting.withdrawableRaw)
    return amount > 0n ? `${fmtShare(Number((out * 10000n) / amount))} released so far` : ''
  }
  const bps = l.lockedShareBps ?? l.shareBps
  if (bps === null || bps === undefined) return ''
  const of = l.shareBasis === 'token-supply' ? 'of the supply' : l.shareBasis === 'lp-supply' ? 'of the LP tokens' : 'of the pool'
  return `${fmtShare(bps)} ${of}`
}

/** The verdict sentence for descriptions and share text. */
export function verdictSentence(l: LockSummary, now: number): string {
  if (l.phase === 'released') return `${l.title} was locked in ${l.type === 'v3' ? 'CADINU Lock V3' : 'CADINU Lock V2'} and has been withdrawn.`
  if (l.phase === 'due') return `${l.title}: the unlock date (${fmtDay(l.unlockAt)}) has passed; the owner can withdraw.`
  const clause = shareClause(l)
  const money = l.lockedUsd !== null && l.lockedUsd > 0 ? `, ${fmtUsd(l.lockedUsd)}` : ''
  if (l.phase === 'vesting') return `${l.title} is released in steps: ${clause}${money}. Fully released by ${fmtDay(l.endAt ?? l.unlockAt)}.`
  return `${l.title} is locked until ${fmtDay(l.unlockAt)}${clause ? ` (${clause}${money})` : ''}, ${fmtSpan(Math.max(0, l.unlockAt - now))} to go.`
}

export function shareText(l: LockSummary, now: number): string {
  return `${verdictSentence(l, now)} Verify it on-chain with CADINU Lock:`
}

export const xShareUrl = (text: string, url: string) => `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`
export const telegramShareUrl = (text: string, url: string) => `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`
// Facebook and LinkedIn take only the link; the card they show comes from the page's Open Graph tags (title, description, image).
export const facebookShareUrl = (url: string) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`
export const linkedinShareUrl = (url: string) => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`
export const whatsappShareUrl = (text: string, url: string) => `https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`
export const redditShareUrl = (title: string, url: string) => `https://www.reddit.com/submit?url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`
/** every share target in one place, so the panel, the tests and the docs agree */
export function shareTargets(l: LockSummary, origin: string, now: number) {
  const url = certificateUrl(origin, l)
  const text = shareText(l, now)
  return [
    { id: 'x', label: 'Post on X', href: xShareUrl(text, url) },
    { id: 'telegram', label: 'Telegram', href: telegramShareUrl(text, url) },
    { id: 'facebook', label: 'Facebook', href: facebookShareUrl(url) },
    { id: 'linkedin', label: 'LinkedIn', href: linkedinShareUrl(url) },
  ] as const
}

export function embedSnippets(origin: string, l: Pick<LockSummary, 'type' | 'id' | 'title'>) {
  const page = certificateUrl(origin, l)
  const alt = `CADINU Lock certificate: ${cleanDescription(l.title)}`.replace(/"/g, '')
  return {
    html: `<a href="${page}"><img src="${badgeUrl(origin, l)}" alt="${alt}" height="28"></a>`,
    htmlCard: `<a href="${page}"><img src="${badgeUrl(origin, l, 'card')}" alt="${alt}" width="320"></a>`,
    markdown: `[![${alt}](${badgeUrl(origin, l)})](${page})`,
  }
}

/** The title and the description of the page for crawlers (Open Graph, X cards, Telegram previews). */
export function seoOf(l: LockSummary, now: number): { title: string; description: string } {
  const head = l.phase === 'released' ? 'Released' : l.phase === 'due' ? 'Unlock date passed' : l.phase === 'vesting' ? 'Releasing in steps' : `Locked until ${fmtDay(l.unlockAt)}`
  return { title: `${l.title}: ${head} · CADINU Lock ${certificateId(l)}`, description: `${verdictSentence(l, now)} Contract ${shortAddress(l.contract)}.` }
}

/** What a vesting lock holds back and what it can pay out, for the certificate's figures (raw units). */
export function vestingFigures(l: LockSummary, now: number) {
  if (!l.vesting) return null
  const t: V2Terms = { amount: BigInt(l.amountRaw), unlockedAmount: BigInt(l.vesting.unlockedRaw), tgeDate: l.unlockAt, tgeBps: l.vesting.tgeBps, cycle: l.vesting.cycleSeconds, cycleBps: l.vesting.cycleBps }
  return { terms: t, vesting: isVesting(t), withdrawableRaw: withdrawableAt(t, now), lockedRaw: lockedAt(t, now) }
}
